import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { Channel } from "../contactLinks";
import type { MyDayTask } from "./MyDay";
import { LossDialog, type LossTarget } from "../pipeline/LossDialog";
import { useOffers } from "../useOffers";
import {
  CLOSING,
  COMMITMENTS,
  logActivity,
  NEXT,
  OUTCOMES,
} from "./logActivity";
import { DictateButton } from "./Dictate";
import { fmtDuration, stopCallTimer, useCallTimer } from "../calls/callTimer";

// Con estos resultados ya se sabe quién decide: se puede renombrar al "Encargado"
const KNOWS_DECISOR = ["hablo_decisor", "respondio", "demo_agendada"];

/**
 * Registro rápido: resultado → siguiente paso → Guardar (≤ 3 toques).
 * Guarda la actividad en contact_notes, marca la tarea hecha y, si aplica,
 * crea la tarea siguiente.
 */
export const QuickLog = ({
  task,
  channel,
  onClose,
}: {
  task: MyDayTask | null;
  channel: Channel;
  onClose: () => void;
}) => {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [commitment, setCommitment] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [decisor, setDecisor] = useState({ name: "", title: "", cel: "" });
  const [saving, setSaving] = useState(false);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const { data: offers } = useOffers();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const timer = useCallTimer();
  const isCall = channel === "llamada";

  useEffect(() => {
    if (isCall && timer.active) setDuration(timer.seconds);
  }, [isCall, timer.active, timer.seconds]);

  const reset = () => {
    setOutcome(null);
    setNext(null);
    setCommitment(null);
    setNote("");
    setDecisor({ name: "", title: "", cel: "" });
    setDuration(null);
  };
  const askDecisor =
    task?.first_name === "Encargado" && KNOWS_DECISOR.includes(outcome ?? "");

  const pickOutcome = (value: string) => {
    setOutcome(value);
    setNext(CLOSING.includes(value) ? 0 : 3);
  };

  const offer = offers?.find((o) => o.slug === task?.offer_slug);

  // "No interesado": primero el motivo de pérdida; si cancela, no se guarda nada
  const trySave = () => {
    if (!task || !outcome || next == null) return;
    if (isCall && timer.active) stopCallTimer();
    if (isCall && duration != null && duration > 90 * 60) {
      if (
        !window.confirm(
          `${fmtDuration(duration)} es una llamada muy larga (posible olvido). ¿Guardar de todas formas?`,
        )
      )
        return;
    }
    if (outcome === "no_interesado" && task.deal_id && offer) {
      setLoss({
        id: task.deal_id,
        key: task.deal_key,
        company_name: task.company_name,
        offer_id: offer.id,
      });
      return;
    }
    save();
  };

  const save = async () => {
    if (!task || !outcome || next == null) return;
    setSaving(true);
    // PRD 1.9: el "Encargado" pasa a ser el decisor (su celular queda primero)
    const [first, ...rest] = decisor.name.trim().split(/\s+/);
    const contactUpdate = {
      ...(askDecisor && first
        ? { first_name: first, last_name: rest.join(" ") }
        : {}),
      ...(askDecisor && decisor.title.trim()
        ? { title: decisor.title.trim() }
        : {}),
      ...(askDecisor && decisor.cel.trim()
        ? {
            phone_jsonb: [
              { number: decisor.cel.trim(), type: "Work" },
              ...(task.phone_jsonb ?? []),
            ],
          }
        : {}),
    };
    try {
      await logActivity({
        contactId: task.contact_id,
        dealId: task.deal_id,
        channel,
        outcome,
        note,
        nextDays: next,
        nextTaskType: task.channel ?? "call",
        nextTaskText: task.text ?? "Seguimiento",
        close: { taskId: task.task_id },
        contactUpdate,
        deal:
          task.stage && offer
            ? { stage: task.stage, stages: offer.stages }
            : undefined,
        durationSeconds: isCall ? duration : null,
        commitment,
        templateId: task.template_id,
      });
      notify("Actividad registrada", { type: "success" });
      reset();
      [
        "my_day",
        "deal_board",
        "funnel_by_offer",
        "weekly_goal",
        "done_today",
        "thermo_alerts",
      ].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
      onClose();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setSaving(false);
  };

  return (
    <>
      <LossDialog
        target={loss}
        onClose={(lost) => {
          setLoss(null);
          if (lost) save();
        }}
      />
      <Sheet
        open={!!task}
        onOpenChange={(open) => {
          if (!open && !saving) {
            reset();
            onClose();
          }
        }}
      >
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader className="pb-0">
            <SheetTitle>¿Cómo te fue?</SheetTitle>
            <SheetDescription>
              {task?.company_name ?? `${task?.first_name} ${task?.last_name}`}
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-4 flex flex-col gap-4">
            {isCall && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {timer.active && (
                  <span className="font-mono font-semibold">
                    {timer.paused ? "Pausado " : ""}
                    {fmtDuration(timer.seconds)}
                  </span>
                )}
                {timer.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      timer.paused ? timer.resume() : timer.pause()
                    }
                  >
                    {timer.paused ? "Reanudar" : "Pausar"}
                  </Button>
                )}
                <label className="flex items-center gap-1.5 text-muted-foreground">
                  ¿Cuánto duró?
                  <Input
                    className="w-20"
                    inputMode="numeric"
                    placeholder="0:00"
                    value={duration != null ? fmtDuration(duration) : ""}
                    onChange={(e) => {
                      const m = e.target.value.match(/^(\d+):?(\d{0,2})$/);
                      setDuration(
                        m ? Number(m[1]) * 60 + Number(m[2] || 0) : null,
                      );
                    }}
                  />
                </label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {OUTCOMES.map((o) => (
                <Button
                  key={o.value}
                  variant={outcome === o.value ? "default" : "outline"}
                  className="h-12 whitespace-normal leading-tight"
                  onClick={() => pickOutcome(o.value)}
                >
                  {o.label}
                </Button>
              ))}
            </div>
            {askDecisor && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">
                  ¿Quién decide? (opcional, reemplaza a "Encargado")
                </p>
                <Input
                  placeholder="Nombre y apellido"
                  value={decisor.name}
                  onChange={(e) =>
                    setDecisor({ ...decisor, name: e.target.value })
                  }
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Puesto"
                    value={decisor.title}
                    onChange={(e) =>
                      setDecisor({ ...decisor, title: e.target.value })
                    }
                  />
                  <Input
                    placeholder="Celular / WhatsApp"
                    inputMode="tel"
                    value={decisor.cel}
                    onChange={(e) =>
                      setDecisor({ ...decisor, cel: e.target.value })
                    }
                  />
                </div>
              </div>
            )}
            <div>
              <p className="text-sm font-medium mb-2">Siguiente paso</p>
              <div className="grid grid-cols-4 gap-2">
                {NEXT.map((n) => (
                  <Button
                    key={n.days}
                    variant={next === n.days ? "default" : "outline"}
                    className="h-12 px-1"
                    onClick={() => setNext(n.days)}
                  >
                    {n.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">
                Compromiso obtenido (opcional)
              </p>
              <div className="grid grid-cols-3 gap-2">
                {COMMITMENTS.map((c) => (
                  <Button
                    key={c.value}
                    variant={commitment === c.value ? "default" : "outline"}
                    className="h-12 whitespace-normal px-1 text-xs leading-tight"
                    onClick={() =>
                      setCommitment(commitment === c.value ? null : c.value)
                    }
                  >
                    {c.label}
                  </Button>
                ))}
              </div>
            </div>
            <DictateButton
              className="self-start"
              onResult={(r) => {
                setNote((n) => (n ? `${n} ${r.note}` : r.note));
                if (r.outcome) pickOutcome(r.outcome);
                if (r.nextDays != null) setNext(r.nextDays);
              }}
            />
            <Textarea
              placeholder="Nota (opcional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
            <Button
              className="h-14 text-base"
              disabled={!outcome || next == null || saving}
              onClick={trySave}
            >
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
